import React, { createContext, useContext, useMemo, useState, useEffect, ReactNode } from 'react';
import { useBrand } from '../../brands/contexts/BrandContext';

type Language = 'ru' | 'en';

interface Translations {
  [key: string]: {
    ru: string;
    en: string;
  };
}

interface I18nContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const LANGUAGE_STORAGE_KEY = 'lazydoc_app_language';

// Базовый словарь. По мере внедрения локализации сюда будут добавляться новые ключи.
const baseTranslations: Translations = {
  'auth.title.login': {
    ru: 'Вход',
    en: 'Sign in',
  },
  'auth.title.register': {
    ru: 'Регистрация',
    en: 'Sign up',
  },
  'auth.login.submit.idle': {
    ru: 'Войти',
    en: 'Sign in',
  },
  'auth.login.submit.loading': {
    ru: 'Вход...',
    en: 'Signing in...',
  },
  'auth.register.submit.idle': {
    ru: 'Зарегистрироваться',
    en: 'Sign up',
  },
  'auth.register.submit.loading': {
    ru: 'Регистрация...',
    en: 'Signing up...',
  },
  'auth.noAccount': {
    ru: 'Нет аккаунта? ',
    en: "Don't have an account? ",
  },
  'auth.haveAccount': {
    ru: 'Уже есть аккаунт? ',
    en: 'Already have an account? ',
  },
  'auth.toRegister': {
    ru: 'Зарегистрироваться',
    en: 'Sign up',
  },
  'auth.toLogin': {
    ru: 'Войти',
    en: 'Sign in',
  },
  'auth.form.requiredNotice': {
    ru: '* Поля с звездочкой обязательны для заполнения',
    en: '* Fields marked with an asterisk are required',
  },
  'auth.field.firstName': {
    ru: 'Имя *',
    en: 'First name *',
  },
  'auth.field.lastName': {
    ru: 'Фамилия *',
    en: 'Last name *',
  },
  'auth.field.specialty': {
    ru: 'Специальность *',
    en: 'Specialty *',
  },
  'auth.loadingSpecialties': {
    ru: 'Загрузка...',
    en: 'Loading...',
  },
  'auth.specialtyPlaceholder': {
    ru: 'Выберите специальность',
    en: 'Select specialty',
  },
  'auth.searchSpecialty': {
    ru: 'Поиск специальности...',
    en: 'Search specialty...',
  },
  'auth.field.phoneOptional': {
    ru: '+79XXXXXXXXX (опционально)',
    en: '+79XXXXXXXXX (optional)',
  },
  'auth.field.email.required': {
    ru: 'Email *',
    en: 'Email *',
  },
  'auth.field.password.required': {
    ru: 'Пароль *',
    en: 'Password *',
  },
  'auth.field.passwordConfirm.required': {
    ru: 'Подтвердите пароль *',
    en: 'Confirm password *',
  },
  'auth.section.personal': {
    ru: 'Личные данные',
    en: 'Personal details',
  },
  'auth.section.contact': {
    ru: 'Контактные данные',
    en: 'Contact details',
  },
  'auth.section.security': {
    ru: 'Безопасность',
    en: 'Security',
  },
  'auth.password.requirements': {
    ru: 'Пароль должен содержать минимум 8 символов, включая заглавные и строчные буквы, а также цифры',
    en: 'Password must be at least 8 characters long and include uppercase and lowercase letters and numbers',
  },
  'auth.code.send.idle': {
    ru: 'Отправить код подтверждения',
    en: 'Send verification code',
  },
  'auth.code.send.loading': {
    ru: 'Отправка...',
    en: 'Sending...',
  },
  'auth.code.send.retry': {
    ru: 'Отправить код повторно',
    en: 'Resend code',
  },
  'auth.code.placeholder': {
    ru: 'Введите 6-значный код *',
    en: 'Enter 6-digit code *',
  },
  'auth.success.title': {
    ru: 'Аккаунт успешно создан!',
    en: 'Account created successfully!',
  },
  'auth.success.line1': {
    ru: 'Ваш аккаунт был зарегистрирован и ожидает активации администратором.',
    en: 'Your account has been registered and is awaiting activation by an administrator.',
  },
  'auth.success.line2': {
    ru: 'Теперь вы можете войти в систему.',
    en: 'You can now sign in to the system.',
  },
  'auth.success.button': {
    ru: 'Войти в систему',
    en: 'Sign in to the system',
  },
  'auth.validation.email.required': {
    ru: 'Email обязателен',
    en: 'Email is required',
  },
  'auth.validation.email.invalid': {
    ru: 'Введите корректный email',
    en: 'Enter a valid email address',
  },
  'auth.validation.password.required': {
    ru: 'Пароль обязателен',
    en: 'Password is required',
  },
  'auth.validation.password.minLength': {
    ru: 'Пароль должен содержать минимум 8 символов',
    en: 'Password must be at least 8 characters long',
  },
  'auth.validation.password.lowercase': {
    ru: 'Пароль должен содержать хотя бы одну строчную букву',
    en: 'Password must contain at least one lowercase letter',
  },
  'auth.validation.password.uppercase': {
    ru: 'Пароль должен содержать хотя бы одну заглавную букву',
    en: 'Password must contain at least one uppercase letter',
  },
  'auth.validation.password.digit': {
    ru: 'Пароль должен содержать хотя бы одну цифру',
    en: 'Password must contain at least one digit',
  },
  'auth.validation.phone.invalid': {
    ru: 'Введите корректный номер телефона (+79XXXXXXXXX)',
    en: 'Enter a valid phone number (+79XXXXXXXXX)',
  },
  'auth.validation.field.required': {
    ru: 'Поле не может быть пустым',
    en: 'Field cannot be empty',
  },
  'auth.validation.passwordConfirm.required': {
    ru: 'Введите пароль',
    en: 'Enter password',
  },
  'auth.validation.passwordConfirm.mismatch': {
    ru: 'Пароли не совпадают',
    en: 'Passwords do not match',
  },
  'auth.validation.code.required': {
    ru: 'Введите код подтверждения',
    en: 'Enter verification code',
  },
  'auth.validation.code.digitsOnly': {
    ru: 'Код должен содержать только цифры',
    en: 'Code must contain digits only',
  },
  'auth.validation.code.length': {
    ru: 'Код должен содержать 6 цифр',
    en: 'Code must be 6 digits long',
  },
  'auth.snackbar.emailInvalid': {
    ru: 'Введите корректный email',
    en: 'Enter a valid email address',
  },
  'auth.snackbar.codeSendError': {
    ru: 'Ошибка отправки кода',
    en: 'Error sending verification code',
  },
  'auth.snackbar.loginError': {
    ru: 'Ошибка авторизации',
    en: 'Login error',
  },
  'auth.snackbar.registerError': {
    ru: 'Ошибка регистрации',
    en: 'Registration error',
  },
  'auth.snackbar.registerSuccess': {
    ru: 'Регистрация выполнена успешно!',
    en: 'Registration completed successfully!',
  },
  'auth.snackbar.nowYouCanLogin': {
    ru: 'Теперь вы можете войти в систему',
    en: 'You can now sign in',
  },
  'auth.snackbar.codeNotRequested': {
    ru: 'Сначала запросите код подтверждения',
    en: 'Request a verification code first',
  },
  'profile.title': {
    ru: 'Настройки',
    en: 'Settings',
  },
  'profile.field.firstName': {
    ru: 'Имя:',
    en: 'First name:',
  },
  'profile.field.lastName': {
    ru: 'Фамилия:',
    en: 'Last name:',
  },
  'profile.field.email': {
    ru: 'Email:',
    en: 'Email:',
  },
  'profile.field.role': {
    ru: 'Роль:',
    en: 'Role:',
  },
  'profile.field.specialty': {
    ru: 'Специальность:',
    en: 'Specialty:',
  },
  'profile.field.profession': {
    ru: 'Специальность',
    en: 'Specialty',
  },
  'profile.professionHint': {
    ru: 'Выберите специальность, чтобы автоматически получить подходящие специальности',
    en: 'Select a specialty to automatically get matching specialties',
  },
  'profile.professionEmpty': {
    ru: 'Не выбрана',
    en: 'Not selected',
  },
  'profile.professionUpdated': {
    ru: 'Специальность обновлена',
    en: 'Specialty updated',
  },
  'profile.field.organization': {
    ru: 'Организация:',
    en: 'Organization:',
  },
  'profile.field.recognitionType': {
    ru: 'Тип распознавания:',
    en: 'Recognition type:',
  },
  'profile.recognition.option1': {
    ru: '1 вариант',
    en: 'Option 1',
  },
  'profile.recognition.option2': {
    ru: '2 вариант',
    en: 'Option 2',
  },
  'profile.logout': {
    ru: 'Выйти из аккаунта',
    en: 'Log out',
  },
  'profile.language.label': {
    ru: 'Язык интерфейса:',
    en: 'Interface language:',
  },
  'profile.language.ru': {
    ru: 'Русский',
    en: 'Russian',
  },
  'profile.language.en': {
    ru: 'Английский',
    en: 'English',
  },
  'profile.avatar.clickToUpload': {
    ru: 'Нажмите для загрузки аватара',
    en: 'Click to upload avatar',
  },
  'profile.avatar.upload': {
    ru: 'Загрузить',
    en: 'Upload',
  },
  'profile.avatar.delete': {
    ru: 'Удалить',
    en: 'Delete',
  },
  'profile.avatar.uploadSuccess': {
    ru: 'Аватар успешно загружен',
    en: 'Avatar uploaded successfully',
  },
  'profile.avatar.deleteSuccess': {
    ru: 'Аватар успешно удалён',
    en: 'Avatar deleted successfully',
  },
  'profile.avatar.confirmDelete': {
    ru: 'Вы уверены, что хотите удалить аватар?',
    en: 'Are you sure you want to delete the avatar?',
  },
  'profile.avatar.error.invalidType': {
    ru: 'Пожалуйста, выберите изображение',
    en: 'Please select an image file',
  },
  'profile.avatar.error.tooLarge': {
    ru: 'Размер файла не должен превышать 5MB',
    en: 'File size must not exceed 5MB',
  },
  'profile.avatar.error.upload': {
    ru: 'Ошибка загрузки аватара',
    en: 'Error uploading avatar',
  },
  'profile.avatar.error.delete': {
    ru: 'Ошибка удаления аватара',
    en: 'Error deleting avatar',
  },

  // Sidebar
  'sidebar.toggle.close': { ru: 'Закрыть сайдбар', en: 'Close sidebar' },
  'sidebar.toggle.open': { ru: 'Открыть сайдбар', en: 'Open sidebar' },
  'sidebar.search.placeholder': { ru: 'Поиск чата', en: 'Search chat' },
  'sidebar.newChat': { ru: 'Новый чат', en: 'New chat' },
  'sidebar.chats': { ru: 'Чаты', en: 'Chats' },
  'sidebar.loadingChats': { ru: 'Загрузка чатов...', en: 'Loading chats...' },
  'sidebar.showMore': { ru: 'Показать ещё', en: 'Show more' },
  'sidebar.noChats': { ru: 'Чаты не найдены', en: 'No chats found' },
  'sidebar.copilot.close': { ru: 'Закрыть Copilot', en: 'Close Copilot' },
  'sidebar.copilot.open': { ru: 'Открыть Copilot', en: 'Open Copilot' },
  'sidebar.copilot': { ru: 'Copilot', en: 'Copilot' },
  'copilot.title': { ru: 'Copilot', en: 'Copilot' },
  'copilot.clearChat': { ru: 'Очистить чат', en: 'Clear chat' },
  'copilot.askQuestion': { ru: 'Задайте вопрос Copilot', en: 'Ask Copilot a question' },
  'copilot.askPlaceholder': { ru: 'Задайте вопрос Copilot...', en: 'Ask Copilot a question...' },
  'copilot.localHint': { ru: 'Этот чат локальный и не сохраняется', en: 'This chat is local and is not saved' },
  'copilot.errorResponse': { ru: 'Извините, произошла ошибка при обработке запроса.', en: 'Sorry, an error occurred while processing your request.' },
  'copilot.errorAssistant': { ru: 'Извините, произошла ошибка при обращении к медицинскому помощнику. Попробуйте еще раз.', en: 'Sorry, an error occurred while contacting the medical assistant. Please try again.' },
  'copilot.options.title': { ru: 'LazyDoc Copilot', en: 'LazyDoc Copilot' },
  'copilot.options.chooseWay': { ru: 'Выберите способ открытия:', en: 'Choose how to open:' },
  'copilot.options.documentPipTitle': { ru: 'Открыть в настоящем Picture-in-Picture (Chrome/Edge 116+)', en: 'Open in real Picture-in-Picture (Chrome/Edge 116+)' },
  'copilot.options.documentPip': { ru: 'Document PiP ⭐', en: 'Document PiP ⭐' },
  'copilot.options.documentPipDesc': { ru: 'Настоящий PiP - поверх всех приложений!', en: 'Real PiP - on top of all apps!' },
  'copilot.options.pipTitle': { ru: 'Открыть в Picture-in-Picture режиме', en: 'Open in Picture-in-Picture mode' },
  'copilot.options.pip': { ru: 'Picture-in-Picture', en: 'Picture-in-Picture' },
  'copilot.options.pipDesc': { ru: 'Плавающее окно на текущей странице', en: 'Floating window on the current page' },
  'copilot.options.popupTitle': { ru: 'Открыть в отдельном окне', en: 'Open in separate window' },
  'copilot.options.popup': { ru: 'Отдельное окно', en: 'Separate window' },
  'copilot.options.popupDesc': { ru: 'Может отображаться поверх других приложений', en: 'Can appear on top of other apps' },
  'copilot.options.tabTitle': { ru: 'Открыть в новой вкладке', en: 'Open in new tab' },
  'copilot.options.tab': { ru: 'Новая вкладка', en: 'New tab' },
  'copilot.options.tabDesc': { ru: 'Полноэкранный режим', en: 'Full screen mode' },
  'copilot.options.recommended': { ru: '🎬 Рекомендуется: Document PiP - настоящий Picture-in-Picture, который отображается поверх всех приложений!', en: '🎬 Recommended: Document PiP - real Picture-in-Picture that appears on top of all apps!' },
  'copilot.options.tip': { ru: '💡 Совет: Для максимального удобства используйте отдельное окно - оно может отображаться поверх других приложений!', en: '💡 Tip: For best experience use a separate window - it can appear on top of other apps!' },
  'copilot.options.note': { ru: 'ℹ️ Примечание: Document PiP доступен только в Chrome/Edge 116+. Обновите браузер для доступа к этой функции.', en: 'ℹ️ Note: Document PiP is only available in Chrome/Edge 116+. Update your browser to use this feature.' },
  'copilot.options.popupBlocked': { ru: 'Всплывающие окна заблокированы браузером. Пожалуйста, разрешите всплывающие окна для этого сайта.', en: 'Pop-ups are blocked by the browser. Please allow pop-ups for this site.' },
  'copilot.options.pipOpenError': { ru: 'Не удалось открыть Picture-in-Picture. Убедитесь, что используете Chrome/Edge 116+ и включили Document PiP API.', en: 'Failed to open Picture-in-Picture. Make sure you use Chrome/Edge 116+ and have Document PiP API enabled.' },
  'sidebar.templates': { ru: 'Шаблоны', en: 'Templates' },
  'sidebar.statistics': { ru: 'Статистика', en: 'Statistics' },
  'sidebar.admin': { ru: 'Администрирование', en: 'Administration' },
  'sidebar.profile': { ru: 'Профиль', en: 'Profile' },
  'sidebar.subscription': { ru: 'Подписка', en: 'Subscription' },

  'subscription.title': { ru: 'Подписка', en: 'Subscription' },
  'subscription.loading': { ru: 'Загрузка...', en: 'Loading...' },
  'subscription.loadError': { ru: 'Не удалось загрузить данные подписки', en: 'Could not load subscription' },
  'subscription.error': { ru: 'Произошла ошибка', en: 'Something went wrong' },
  'subscription.noSubscription': { ru: 'У вас пока нет активной подписки. Оформите подписку, чтобы пользоваться лимитами протоколов.', en: 'You have no active subscription yet.' },
  'subscription.chooseTariff': { ru: 'Выбрать тариф', en: 'Choose a plan' },
  'subscription.currentPlan': { ru: 'Текущий вариант подписки', en: 'Current plan' },
  'subscription.nextPayment': { ru: 'Следующий платёж', en: 'Next payment' },
  'subscription.limitLeft': { ru: 'Остаток лимита', en: 'Remaining limit' },
  'subscription.of': { ru: 'из', en: 'of' },
  'subscription.resetLimitTitle': { ru: 'Обновление лимита протоколов', en: 'Protocol limit refresh' },
  'subscription.resetLimitDesc': {
    ru: 'После успешной оплаты лимит обновится, дата следующего платежа сдвинется на месяц вперёд.',
    en: 'After successful payment your limit refreshes and the next billing date moves forward by one month.',
  },
  'subscription.resetLimitBtn': { ru: 'Перейти к оплате', en: 'Proceed to payment' },
  'subscription.resetLimitsOk': { ru: 'Лимит обновлён', en: 'Limit updated' },
  'subscription.resetLimitsPaymentError': {
    ru: 'Не удалось создать платёж за обновление лимита',
    en: 'Could not start payment to refresh your limit',
  },
  'subscription.changeTariffTitle': { ru: 'Смена тарифа', en: 'Change plan' },
  'subscription.changeTariffDesc': { ru: 'Для получения увеличенных лимитов формирования мед. протоколов', en: 'To get higher limits for medical protocols' },
  'subscription.changeTariffBtn': { ru: 'Смена тарифа', en: 'Change plan' },
  'subscription.autoRenew': { ru: 'Автопродление', en: 'Auto-renewal' },
  'subscription.autoRenewOn': { ru: 'Включено', en: 'On' },
  'subscription.autoRenewOff': { ru: 'Выключено', en: 'Off' },
  'subscription.cancelSectionTitle': { ru: 'Автопродление и отмена', en: 'Auto-renewal & cancellation' },
  'subscription.cancelSectionDesc': {
    ru: 'Отключите автопродление, если не хотите продлевать подписку. Доступ сохранится до конца оплаченного периода.',
    en: 'Turn off auto-renewal if you don’t want to renew. Access remains until the end of the paid period.',
  },
  'subscription.cancelSubscription': { ru: 'Отключить автопродление', en: 'Turn off auto-renewal' },
  'subscription.cancelConfirm': { ru: 'Отключить автопродление? Подписка останется активной до конца оплаченного периода.', en: 'Turn off auto-renewal? Your plan stays active until the end of the paid period.' },
  'subscription.cancelOk': { ru: 'Автопродление отключено', en: 'Auto-renewal turned off' },
  'subscription.selectTariffTitle': { ru: 'Выбор тарифа', en: 'Choose a plan' },
  'subscription.tariffsToolbarHint': { ru: 'Выберите период оплаты и тариф', en: 'Choose billing period and a plan' },
  'subscription.periodMonthly': { ru: 'Ежемесячная', en: 'Monthly' },
  'subscription.periodYearly': { ru: 'Ежегодная', en: 'Yearly' },
  'subscription.currentBadge': { ru: 'Текущий', en: 'Current' },
  'subscription.paymentTitle': { ru: 'Оплата', en: 'Payment' },
  'subscription.paymentMethod': { ru: 'Способ оплаты', en: 'Payment method' },
  'subscription.cardPayment': { ru: 'Банковской картой', en: 'Bank card' },
  'subscription.cardPaymentHint': { ru: 'Привязка новой карты к аккаунту', en: 'Link a new card to your account' },
  'subscription.payButton': { ru: 'Оплатить', en: 'Pay' },
  'subscription.payLegal': { ru: 'Нажимая кнопку «Оплатить», вы соглашаетесь с условиями регулярной оплаты', en: 'By clicking Pay you agree to recurring billing terms' },
  'subscription.paymentStartError': { ru: 'Не удалось начать оплату', en: 'Could not start payment' },
  'subscription.tariffNotFound': { ru: 'Тариф не найден', en: 'Plan not found' },
  'subscription.confirmingPayment': { ru: 'Подтверждаем оплату…', en: 'Confirming payment…' },
  'subscription.paymentSuccess': { ru: 'Оплата прошла успешно', en: 'Payment successful' },
  'subscription.paymentSuccessRedirecting': { ru: 'Переходим в приложение…', en: 'Taking you to the app…' },
  'subscription.paymentNoOrder': { ru: 'Не указан номер заказа', en: 'Order id missing' },
  'subscription.paymentConfirmTimeout': { ru: 'Не удалось подтвердить оплату за отведённое время. Проверьте подписку позже.', en: 'Could not confirm payment in time. Check your subscription later.' },
  'subscription.paymentErrorTitle': { ru: 'Ошибка', en: 'Error' },
  'subscription.backToSubscription': { ru: 'К подписке', en: 'Back to subscription' },
  'subscription.backToTariffs': { ru: 'К выбору тарифа', en: 'Back to plans' },
  'subscription.paymentFailTitle': { ru: 'Оплата не прошла', en: 'Payment failed' },
  'subscription.paymentFailBody': { ru: 'Платёж был отклонён или отменён. Вы можете выбрать тариф и попробовать снова.', en: 'The payment was declined or cancelled. You can choose a plan and try again.' },
  'subscription.tryAgain': { ru: 'Попробовать снова', en: 'Try again' },

  // AdminPage
  'admin.title': { ru: 'Администрирование', en: 'Administration' },
  'admin.tryAgain': { ru: 'Попробовать снова', en: 'Try again' },
  'admin.tab.users': { ru: 'Пользователи', en: 'Users' },
  'admin.tab.tariffs': { ru: 'Тарифы', en: 'Tariffs' },
  'admin.tab.organizations': { ru: 'Организации', en: 'Organizations' },
  'admin.tab.specialties': { ru: 'Специальности', en: 'Specialties' },
  'admin.tab.aiFeedback': { ru: 'Оценка ИИ', en: 'AI feedback' },
  'admin.users.title': { ru: 'Управление пользователями', en: 'User management' },
  'admin.users.add': { ru: 'Добавить пользователя', en: 'Add user' },
  'admin.users.bulkDelete': { ru: 'Удалить выбранных', en: 'Delete selected' },
  'admin.users.bulkDeleteConfirm': {
    ru: 'Удалить {count} выбранных пользователей? Это действие необратимо.',
    en: 'Delete {count} selected user(s)? This cannot be undone.',
  },
  'admin.users.bulkDeleteSuccess': { ru: 'Удалено пользователей: {count}', en: 'Deleted users: {count}' },
  'admin.users.bulkDeletePartialError': {
    ru: 'Не удалось удалить {failed} из {total}',
    en: 'Failed to delete {failed} of {total}',
  },
  'admin.users.bulkDeleteError': { ru: 'Ошибка удаления', en: 'Delete failed' },
  'admin.users.selectAllOnPage': { ru: 'Выбрать всех на странице', en: 'Select all on this page' },
  'admin.users.selectRow': { ru: 'Выбрать пользователя', en: 'Select user' },
  'admin.users.cannotDeleteSelf': {
    ru: 'Нельзя удалить свою учётную запись',
    en: 'You cannot delete your own account',
  },
  'admin.search.placeholder': { ru: 'Поиск по имени, фамилии или email', en: 'Search by name or email' },
  'admin.filters': { ru: 'Фильтры', en: 'Filters' },
  'admin.filter.allRoles': { ru: 'Все роли', en: 'All roles' },
  'admin.filter.allOrganizations': { ru: 'Все организации', en: 'All organizations' },
  'admin.filter.allStatuses': { ru: 'Все статусы', en: 'All statuses' },
  'admin.filter.active': { ru: 'Активные', en: 'Active' },
  'admin.filter.inactive': { ru: 'Неактивные', en: 'Inactive' },
  'admin.filter.recordsCount': { ru: 'Количество записей', en: 'Records count' },
  'admin.filter.records10': { ru: '10 записей', en: '10 records' },
  'admin.filter.records25': { ru: '25 записей', en: '25 records' },
  'admin.filter.records50': { ru: '50 записей', en: '50 records' },
  'admin.filter.records100': { ru: '100 записей', en: '100 records' },
  'admin.filter.role': { ru: 'Роль', en: 'Role' },
  'admin.filter.organization': { ru: 'Организация', en: 'Organization' },
  'admin.filter.accountStatus': { ru: 'Статус аккаунта', en: 'Account status' },
  'admin.loading': { ru: 'Загрузка...', en: 'Loading...' },
  'admin.table.select': { ru: 'Выбор', en: 'Select' },
  'admin.table.user': { ru: 'Пользователь', en: 'User' },
  'admin.table.email': { ru: 'Email', en: 'Email' },
  'admin.table.subscription': { ru: 'Подписка', en: 'Subscription' },
  'admin.table.specialty': { ru: 'Специальность', en: 'Specialty' },
  'admin.table.organization': { ru: 'Организация', en: 'Organization' },
  'admin.table.role': { ru: 'Роль', en: 'Role' },
  'admin.table.accountStatus': { ru: 'Статус аккаунта', en: 'Account status' },
  'admin.subscription.active': { ru: 'Активна', en: 'Active' },
  'admin.subscription.canceled': { ru: 'Отменена', en: 'Canceled' },
  'admin.subscription.expired': { ru: 'Истекла', en: 'Expired' },
  'admin.subscription.none': { ru: 'Нет', en: 'None' },
  'admin.activation.active': { ru: 'Активен', en: 'Active' },
  'admin.activation.inactive': { ru: 'Неактивен', en: 'Inactive' },
  'admin.activation.deactivate': { ru: 'Нажмите для деактивации', en: 'Click to deactivate' },
  'admin.activation.activate': { ru: 'Нажмите для активации', en: 'Click to activate' },
  'admin.pagination.prev': { ru: '← Предыдущая', en: '← Previous' },
  'admin.pagination.next': { ru: 'Следующая →', en: 'Next →' },
  'admin.pagination.showingPrefix': { ru: 'Показано', en: 'Showing' },
  'admin.pagination.showingSuffix': { ru: 'из', en: 'of' },
  'admin.error.loadUsers': { ru: 'Ошибка загрузки пользователей', en: 'Error loading users' },
  'admin.feedback.title': { ru: 'Оценка ответов ИИ', en: 'AI response feedback' },
  'admin.feedback.sortBy': { ru: 'Сортировка', en: 'Sort by' },
  'admin.feedback.sort.time': { ru: 'По времени', en: 'By time' },
  'admin.feedback.sort.status': { ru: 'По статусу', en: 'By status' },
  'admin.feedback.order': { ru: 'Порядок', en: 'Order' },
  'admin.feedback.order.asc': { ru: 'По возрастанию', en: 'Ascending' },
  'admin.feedback.order.desc': { ru: 'По убыванию', en: 'Descending' },
  'admin.feedback.organization': { ru: 'Организация', en: 'Organization' },
  'admin.feedback.organization.all': { ru: 'Все организации', en: 'All organizations' },
  'admin.feedback.limit': { ru: 'Лимит', en: 'Limit' },
  'admin.feedback.table.user': { ru: 'Пользователь', en: 'User' },
  'admin.feedback.table.organization': { ru: 'Организация', en: 'Organization' },
  'admin.feedback.table.specialty': { ru: 'Специальность', en: 'Specialty' },
  'admin.feedback.table.messageId': { ru: 'Message ID', en: 'Message ID' },
  'admin.feedback.table.chatId': { ru: 'Chat ID', en: 'Chat ID' },
  'admin.feedback.table.aiText': { ru: 'Ответ ИИ', en: 'AI response' },
  'admin.feedback.table.comment': { ru: 'Комментарий', en: 'Comment' },
  'admin.feedback.table.status': { ru: 'Статус', en: 'Status' },
  'admin.feedback.table.createdAt': { ru: 'Создан', en: 'Created at' },
  'admin.feedback.table.actions': { ru: 'Действия', en: 'Actions' },
  'admin.feedback.status.unresolved': { ru: 'Не решено', en: 'Unresolved' },
  'admin.feedback.status.resolved': { ru: 'Решено', en: 'Resolved' },
  'admin.feedback.action.resolve': { ru: 'Отметить решенным', en: 'Mark as resolved' },
  'admin.feedback.action.unresolve': { ru: 'Вернуть в нерешенные', en: 'Mark as unresolved' },
  'admin.feedback.empty': { ru: 'Отзывов пока нет', en: 'No feedback yet' },
  'admin.feedback.viewFull': { ru: 'Показать полностью', en: 'View full' },
  'admin.feedback.aiResponseFull': { ru: 'Ответ ИИ (полный текст)', en: 'AI response (full text)' },
  'admin.feedback.error.load': { ru: 'Ошибка загрузки отзывов', en: 'Failed to load feedback' },
  'admin.feedback.error.status': { ru: 'Ошибка изменения статуса', en: 'Failed to update status' },
  'admin.role.1': { ru: 'Пользователь', en: 'User' },
  'admin.role.2': { ru: 'Модератор', en: 'Moderator' },
  'admin.role.3': { ru: 'Руководитель', en: 'Manager' },
  'admin.role.4': { ru: 'Администратор', en: 'Administrator' },
  'admin.role.unknown': { ru: 'Неизвестная роль', en: 'Unknown role' },

  // StatisticsPage
  'statistics.title': { ru: 'Статистика', en: 'Statistics' },
  'statistics.tryAgain': { ru: 'Попробовать снова', en: 'Try again' },
  'statistics.overall': { ru: 'Общая статистика', en: 'Overall statistics' },
  'statistics.usersCount': { ru: 'Пользователей', en: 'Users' },
  'statistics.chatsCount': { ru: 'Чатов', en: 'Chats' },
  'statistics.protocolsCount': { ru: 'Протоколов', en: 'Protocols' },
  'statistics.bySpecialty': { ru: 'Статистика по специальностям', en: 'Statistics by specialty' },
  'statistics.specialty.users': { ru: 'Пользователей:', en: 'Users:' },
  'statistics.specialty.chats': { ru: 'Чатов:', en: 'Chats:' },
  'statistics.specialty.protocols': { ru: 'Протоколов:', en: 'Protocols:' },
  'statistics.specialty.avgChats': { ru: 'Среднее чатов на пользователя:', en: 'Avg chats per user:' },
  'statistics.specialty.avgProtocols': { ru: 'Среднее протоколов на пользователя:', en: 'Avg protocols per user:' },
  'statistics.usersTitle': { ru: 'Статистика пользователей', en: 'User statistics' },
  'statistics.filter.allSpecialties': { ru: 'Все специальности', en: 'All specialties' },
  'statistics.filter.recordsCount': { ru: 'Количество записей', en: 'Records count' },
  'statistics.table.user': { ru: 'Пользователь', en: 'User' },
  'statistics.table.email': { ru: 'Email', en: 'Email' },
  'statistics.table.specialty': { ru: 'Специальность', en: 'Specialty' },
  'statistics.table.chatsCreated': { ru: 'Чатов создано', en: 'Chats created' },
  'statistics.table.protocolsGenerated': { ru: 'Протоколов сгенерировано', en: 'Protocols generated' },
  'statistics.table.logins': { ru: 'Входов в систему', en: 'Logins' },
  'statistics.table.lastActivity': { ru: 'Последняя активность', en: 'Last activity' },
  'statistics.loading': { ru: 'Загрузка...', en: 'Loading...' },
  'statistics.notSpecified': { ru: 'Не указано', en: 'Not specified' },
  'statistics.pagination.prev': { ru: '← Предыдущая', en: '← Previous' },
  'statistics.pagination.next': { ru: 'Следующая →', en: 'Next →' },
  'statistics.pagination.showingPrefix': { ru: 'Показано', en: 'Showing' },
  'statistics.pagination.showingSuffix': { ru: 'из', en: 'of' },
  'statistics.error.overall': { ru: 'Ошибка загрузки общей статистики', en: 'Error loading overall statistics' },
  'statistics.error.users': { ru: 'Ошибка загрузки статистики пользователей', en: 'Error loading user statistics' },
  'statistics.error.unknown': { ru: 'Неизвестная ошибка', en: 'Unknown error' },

  // Dialogs / Chat
  'dialogs.newChat': { ru: 'Новый чат', en: 'New chat' },
  'dialogs.selectOrStart': { ru: 'Выберите чат или начните новый', en: 'Select a chat or start a new one' },
  'dialogs.rename.title': { ru: 'Нажмите для переименования', en: 'Click to rename' },
  'dialogs.status.offline': { ru: 'Офлайн режим', en: 'Offline mode' },
  'dialogs.status.syncing': { ru: 'Синхронизация...', en: 'Syncing...' },
  'dialogs.status.connected': { ru: 'Подключено к серверу', en: 'Connected to server' },
  'dialogs.status.disconnected': { ru: 'Не подключено к серверу', en: 'Not connected to server' },
  'dialogs.renameChat': { ru: 'Переименовать чат', en: 'Rename chat' },
  'dialogs.renameChatTitle': { ru: 'Название чата:', en: 'Chat title:' },
  'dialogs.renamePlaceholder': { ru: 'Введите название чата', en: 'Enter chat title' },
  'dialogs.renameEmpty': { ru: 'Название чата не может быть пустым', en: 'Chat title cannot be empty' },
  'dialogs.renameError': { ru: 'Не удалось переименовать чат. Попробуйте еще раз.', en: 'Failed to rename chat. Please try again.' },
  'dialogs.cancel': { ru: 'Отмена', en: 'Cancel' },
  'dialogs.save': { ru: 'Сохранить', en: 'Save' },
  'dialogs.saving': { ru: 'Сохранение...', en: 'Saving...' },
  'dialogs.input.placeholder': { ru: 'Введите данные для формирования протокола', en: 'Enter data to generate protocol' },
  'dialogs.attachAudio': { ru: 'Прикрепить аудио файл', en: 'Attach audio file' },
  'dialogs.recordingCancel': { ru: 'Отмена', en: 'Cancel' },
  'dialogs.finishingRecord': { ru: 'Завершение записи...', en: 'Finishing recording...' },
  'dialogs.transcribing': { ru: 'Транскрибация...', en: 'Transcribing...' },
  'dialogs.searchingSpecialty': { ru: 'Поиск специальности...', en: 'Searching specialty...' },
  'dialogs.aiTyping': { ru: 'AI печатает...', en: 'AI typing...' },
  'dialogs.generateProtocol': { ru: 'Сформировать протокол', en: 'Generate protocol' },
  'dialogs.sendToMIS': { ru: 'Передать в МИС', en: 'Send to MIS' },
  'dialogs.recognizedTitle': { ru: 'Распознанный текст', en: 'Recognized text' },
  'dialogs.waitingRecognition': { ru: 'Ожидаем распознавание...', en: 'Waiting for recognition...' },
  'dialogs.voiceMessage': { ru: 'Голосовое сообщение', en: 'Voice message' },
  'dialogs.noSpecialties': { ru: 'У вас нет выбранных специальностей. Обратитесь к администратору для настройки специальностей.', en: 'You have no selected specialties. Contact the administrator to set up specialties.' },
  'dialogs.specialtiesError': { ru: 'Ошибка при загрузке специальностей', en: 'Error loading specialties' },
  'dialogs.protocolError': { ru: 'Неизвестная ошибка при генерации протокола', en: 'Unknown error generating protocol' },
  'dialogs.misSuccess': { ru: 'Протокол успешно отправлен в МИС', en: 'Protocol sent to MIS successfully' },
  'dialogs.misError': { ru: 'Ошибка отправки в МИС', en: 'Error sending to MIS' },
  'dialogs.protocolCopied': { ru: 'Протокол скопирован в буфер обмена', en: 'Protocol copied to clipboard' },
  'dialogs.copyFailed': { ru: 'Не удалось скопировать', en: 'Failed to copy' },
  'dialogs.pdfError': { ru: 'Ошибка генерации PDF файла', en: 'Error generating PDF file' },
  'dialogs.docxError': { ru: 'Ошибка генерации DOCX файла', en: 'Error generating DOCX file' },
  'dialogs.protocolNotFound': { ru: 'Ошибка: протокол не найден', en: 'Error: protocol not found' },
  'dialogs.micNoDevice': { ru: 'Микрофон не найден. Подключите устройство ввода звука и попробуйте снова.', en: 'No microphone found. Please connect an audio input device and try again.' },
  'dialogs.micPermissionDenied': { ru: 'Доступ к микрофону запрещён. Разрешите доступ в настройках браузера.', en: 'Microphone access denied. Please allow access in your browser settings.' },
  'dialogs.micError': { ru: 'Не удалось получить доступ к микрофону.', en: 'Failed to access microphone.' },
  'dialogs.sender.you': { ru: 'Вы', en: 'You' },
  'dialogs.sender.ai': { ru: 'AI', en: 'AI' },
  'dialogs.message.invalidProtocol': { ru: 'Сообщение содержит некорректные данные протокола', en: 'Message contains invalid protocol data' },
  'dialogs.close': { ru: 'Закрыть', en: 'Close' },
  'dialogs.feedback.like': { ru: 'Понравился ответ', en: 'Like response' },
  'dialogs.feedback.dislike': { ru: 'Не понравился ответ', en: 'Dislike response' },
  'dialogs.feedback.title': { ru: 'Оценка ответа ИИ', en: 'AI response feedback' },
  'dialogs.feedback.commentLabel': { ru: 'Комментарий (необязательно)', en: 'Comment (optional)' },
  'dialogs.feedback.commentPlaceholder': { ru: 'Опишите, что было не так (по желанию)', en: 'Describe what was wrong (optional)' },
  'dialogs.feedback.submit': { ru: 'Отправить', en: 'Submit' },
  'dialogs.feedback.sending': { ru: 'Отправка...', en: 'Submitting...' },
  'dialogs.feedback.thanksLike': { ru: 'Спасибо за обратную связь', en: 'Thanks for your feedback' },
  'dialogs.feedback.sent': { ru: 'Спасибо! Отзыв отправлен', en: 'Thanks! Feedback submitted' },
  'dialogs.feedback.sendError': { ru: 'Не удалось отправить отзыв', en: 'Failed to submit feedback' },
  'dialogs.feedback.emptyAiResponse': { ru: 'Нет текста ответа ИИ для отправки', en: 'No AI response text to submit' },

  // Protocols / Templates
  'protocols.myTemplates': { ru: 'Мои шаблоны', en: 'My templates' },
  'protocols.loadTemplatesError': { ru: 'Не удалось загрузить шаблоны', en: 'Failed to load templates' },
  'protocols.loadTemplatesRetry': { ru: 'Возникла проблема при загрузке данных, повторите попытку позже', en: 'A problem occurred while loading data, please try again later' },
  'protocols.refresh': { ru: 'Обновить', en: 'Refresh' },
  'protocols.loading': { ru: 'Загрузка шаблонов...', en: 'Loading templates...' },
  'protocols.management': { ru: 'Управление шаблонами', en: 'Template management' },
  'protocols.searchPlaceholder': { ru: 'Поиск по названию или описанию...', en: 'Search by name or description...' },
  'protocols.filter.allSpecialties': { ru: 'Все специальности', en: 'All specialties' },
  'protocols.filter.allStatuses': { ru: 'Все статусы', en: 'All statuses' },
  'protocols.filter.activeOnly': { ru: 'Только активные', en: 'Active only' },
  'protocols.filter.inactiveOnly': { ru: 'Только неактивные', en: 'Inactive only' },
  'protocols.resetFilters': { ru: 'Сбросить фильтры', en: 'Reset filters' },
  'protocols.activeFilters': { ru: 'Активные фильтры:', en: 'Active filters:' },
  'protocols.filter.specialty': { ru: 'Специальность:', en: 'Specialty:' },
  'protocols.filter.status': { ru: 'Статус:', en: 'Status:' },
  'protocols.filter.search': { ru: 'Поиск:', en: 'Search:' },
  'protocols.createTemplate': { ru: 'Создать шаблон протокола', en: 'Create protocol template' },
  'protocols.fromDocument': { ru: 'Из документа', en: 'From document' },
  'protocols.manually': { ru: 'Вручную', en: 'Manually' },
  'protocols.empty': { ru: 'У вас пока нет шаблонов', en: 'You have no templates yet' },
  'protocols.emptyHint': { ru: 'Создайте свой первый шаблон для начала работы', en: 'Create your first template to get started' },
  'protocols.showingPrefix': { ru: 'Показано', en: 'Showing' },
  'protocols.showingSuffix': { ru: 'из', en: 'of' },
  'protocols.navBack': { ru: '< Назад', en: '< Back' },
  'protocols.navNext': { ru: 'Далее >', en: 'Next >' },
  'protocols.navAria': { ru: 'Навигация по страницам', en: 'Page navigation' },
  'protocols.deleteConfirmTitle': { ru: 'Подтверждение удаления', en: 'Confirm deletion' },
  'protocols.deleteConfirmText': { ru: 'Вы уверены, что хотите удалить этот шаблон? Это действие нельзя отменить.', en: 'Are you sure you want to delete this template? This action cannot be undone.' },
  'protocols.deleting': { ru: 'Удаление...', en: 'Deleting...' },
  'protocols.delete': { ru: 'Удалить', en: 'Delete' },
  'protocols.selectProtocol': { ru: 'Выберите протокол', en: 'Select protocol' },
  'protocols.loadingProtocols': { ru: 'Загрузка протоколов...', en: 'Loading protocols...' },
  'protocols.retry': { ru: 'Попробовать снова', en: 'Try again' },
  'protocols.noActive': { ru: 'У вас нет активных протоколов', en: 'You have no active protocols' },
  'protocols.noActiveHint': { ru: 'Создайте протокол в разделе "Протоколы"', en: 'Create a protocol in the Protocols section' },
  'protocols.specialtyLabel': { ru: 'Специальность:', en: 'Specialty:' },
  'protocols.lastUpdate': { ru: 'Последнее обновление:', en: 'Last update:' },
  'protocols.statusActive': { ru: 'Активный', en: 'Active' },
  'protocols.formGenerate': { ru: 'Сформировать протокол', en: 'Generate protocol' },
  'protocols.forming': { ru: 'Формирование...', en: 'Generating...' },
  'protocols.preview.description': { ru: 'Описание', en: 'Description' },
  'protocols.preview.instructions': { ru: 'Инструкции', en: 'Instructions' },
  'protocols.preview.structure': { ru: 'Структура шаблона', en: 'Template structure' },
  'protocols.preview.created': { ru: 'Создан:', en: 'Created:' },
  'protocols.preview.updated': { ru: 'Обновлен:', en: 'Updated:' },
  'protocols.preview.fieldsCount': { ru: 'Количество полей:', en: 'Fields count:' },
  'protocols.preview.close': { ru: 'Закрыть', en: 'Close' },
  'protocols.preview.active': { ru: 'Активен', en: 'Active' },
  'protocols.preview.inactive': { ru: 'Неактивен', en: 'Inactive' },
  'protocols.preview.required': { ru: 'Обязательное', en: 'Required' },
  'protocols.preview.default': { ru: 'По умолчанию:', en: 'Default:' },
  'protocols.preview.choices': { ru: 'Варианты выбора:', en: 'Choices:' },
  'protocols.preview.range': { ru: 'Диапазон:', en: 'Range:' },
  'protocols.preview.extraFields': { ru: 'Дополнительные поля', en: 'Additional fields' },
  'protocols.preview.typeText': { ru: 'Текст', en: 'Text' },
  'protocols.preview.typeNumber': { ru: 'Число', en: 'Number' },
  'protocols.preview.typeSelect': { ru: 'Выбор', en: 'Select' },
  'protocols.preview.typeCheckbox': { ru: 'Чекбокс', en: 'Checkbox' },
  'protocols.preview.typeDate': { ru: 'Дата', en: 'Date' },
  'protocols.edit.title': { ru: 'Редактирование шаблона протокола', en: 'Edit protocol template' },
  'protocols.edit.loading': { ru: 'Загрузка шаблона протокола...', en: 'Loading protocol template...' },
  'protocols.edit.notFound': { ru: 'Шаблон не найден', en: 'Template not found' },
  'protocols.edit.notFoundDesc': { ru: 'Шаблон с указанным ID не существует или недоступен', en: 'The template with this ID does not exist or is not available' },
  'protocols.edit.backToList': { ru: 'Вернуться к списку', en: 'Back to list' },
  'protocols.edit.saveChanges': { ru: 'Сохранить изменения', en: 'Save changes' },
  'protocols.edit.mainInfo': { ru: 'Основная информация', en: 'Main information' },
  'protocols.edit.templateName': { ru: 'Название шаблона *', en: 'Template name *' },
  'protocols.edit.namePlaceholder': { ru: 'Введите название протокола', en: 'Enter protocol name' },
  'protocols.edit.description': { ru: 'Описание', en: 'Description' },
  'protocols.edit.descriptionPlaceholder': { ru: 'Краткое описание протокола', en: 'Brief protocol description' },
  'protocols.edit.specialty': { ru: 'Специальность', en: 'Specialty' },
  'protocols.edit.specialtyPlaceholder': { ru: 'Укажите медицинскую специальность (например: терапия, кардиология)', en: 'Specify medical specialty (e.g. therapy, cardiology)' },
  'protocols.edit.instructions': { ru: 'Общие инструкции по шаблону', en: 'General template instructions' },
  'protocols.edit.instructionsPlaceholder': { ru: 'Введите общие инструкции для заполнения шаблона', en: 'Enter general instructions for filling the template' },
  'protocols.edit.tips': { ru: 'Подсказки', en: 'Tips' },
  'protocols.edit.tip1': { ru: 'Используйте блоки для группировки связанных полей', en: 'Use blocks to group related fields' },
  'protocols.edit.tip2': { ru: 'Настройте типы полей в зависимости от данных', en: 'Set field types according to your data' },
  'protocols.edit.tip3': { ru: 'Отмечайте обязательные поля для валидации', en: 'Mark required fields for validation' },
  'protocols.builder.title': { ru: 'Конструктор шаблона протоколов', en: 'Protocol template builder' },
  'protocols.builder.description': { ru: 'Создайте структуру протокола, добавляя блоки и поля. JSON будет сгенерирован автоматически.', en: 'Create the protocol structure by adding blocks and fields. JSON will be generated automatically.' },
  'protocols.builder.addBlock': { ru: 'Добавить блок', en: 'Add block' },
  'protocols.create.title': { ru: 'Создание протокола', en: 'Create protocol' },
  'protocols.create.uploadDoc': { ru: 'Загрузить документ', en: 'Upload document' },
  'protocols.create.create': { ru: 'Создать протокол', en: 'Create protocol' },
  'protocols.create.creating': { ru: 'Создание...', en: 'Creating...' },
  'protocols.create.fromDocBanner': { ru: 'Протокол создан из документа', en: 'Protocol created from document' },
  'protocols.create.accuracy': { ru: 'Точность распознавания:', en: 'Recognition accuracy:' },
  'protocols.instruments.sendToMIS': { ru: 'Передать в МИС', en: 'Send to MIS' },
  'protocols.instruments.exportPdf': { ru: 'Сформировать PDF файл', en: 'Generate PDF file' },
  'protocols.instruments.exportDocx': { ru: 'Сформировать DOCX файл', en: 'Generate DOCX file' },
};

export const I18nProvider = ({ children }: { children: ReactNode }): React.ReactElement => {
  const { brand } = useBrand();

  const detectInitialLanguage = (): Language => {
    if (typeof window === 'undefined') {
      return brand.i18n.defaultLanguage;
    }

    try {
      const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null;
      if (stored === 'ru' || stored === 'en') {
        return stored;
      }
    } catch {
      // ignore storage errors
    }

    const navigatorLang =
      (typeof navigator !== 'undefined' && (navigator.language || (navigator as any).userLanguage)) || '';

    if (navigatorLang.toLowerCase().startsWith('ru')) {
      return 'ru';
    }

    // Если системный язык не русский — по требованиям используем английский
    return 'en';
  };

  const [language, setLanguageState] = useState<Language>(() => detectInitialLanguage());

  useEffect(() => {
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch {
      // ignore
    }
  }, [language]);

  const setLanguage = (lang: Language) => {
    if (!brand.i18n.supportedLanguages.includes(lang)) {
      return;
    }
    setLanguageState(lang);
  };

  const value = useMemo<I18nContextValue>(() => {
    const t = (key: string): string => {
      const entry = baseTranslations[key];
      if (!entry) {
        // Если перевод не найден — возвращаем ключ для упрощения отладки
        return key;
      }
      return entry[language] ?? entry[brand.i18n.defaultLanguage] ?? key;
    };

    return {
      language,
      setLanguage,
      t,
    };
  }, [language, brand.i18n.defaultLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextValue => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return ctx;
};

